import { useState, useEffect, useCallback } from 'react';
import { en } from './en';
import { es } from './es';
import { pt } from './pt';
import { zh } from './zh';
import { hi } from './hi';
import { ar } from './ar';
import { ko } from './ko';
import { ja } from './ja';
import { bn } from './bn';
import { de } from './de';
import { fr } from './fr';

export type Language = 'en' | 'es' | 'pt' | 'zh' | 'hi' | 'ar' | 'ko' | 'ja' | 'bn' | 'de' | 'fr';
export type TranslationKey = keyof typeof en;

const translations: Record<Language, typeof en> = {
    en,
    es,
    pt,
    zh,
    hi,
    ar,
    ko,
    ja,
    bn,
    de,
    fr,
};

/**
 * Detects the browser's preferred language
 * Returns detected language code or 'en' as fallback
 */
const detectBrowserLanguage = (): Language => {
    // Check localStorage first for user preference
    const storedLanguage = localStorage.getItem('preferredLanguage') as Language | null;
    const validLanguages: Language[] = ['en', 'es', 'pt', 'zh', 'hi', 'ar', 'ko', 'ja', 'bn', 'de', 'fr'];
    if (storedLanguage && validLanguages.includes(storedLanguage)) {
        return storedLanguage;
    }

    // Get browser languages
    const browserLanguages = navigator.languages || [navigator.language];
    
    // Check browser languages against supported languages
    for (const lang of browserLanguages) {
        const langCode = lang.toLowerCase().split('-')[0];
        if (langCode === 'es') return 'es';
        if (langCode === 'pt') return 'pt';
        if (langCode === 'zh') return 'zh';
        if (langCode === 'hi') return 'hi';
        if (langCode === 'ar') return 'ar';
        if (langCode === 'ko') return 'ko';
        if (langCode === 'ja') return 'ja';
        if (langCode === 'bn') return 'bn';
        if (langCode === 'de') return 'de';
        if (langCode === 'fr') return 'fr';
    }
    
    // Default to English
    return 'en';
};

/**
 * Custom hook for internationalization with browser language detection
 */
export const useLocalization = () => {
    const [language, setLanguageState] = useState<Language>(() => detectBrowserLanguage());

    // Update localStorage when language changes
    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('preferredLanguage', lang);
    }, []);

    // Toggle between languages
    const toggleLanguage = useCallback(() => {
        setLanguage(language === 'en' ? 'es' : 'en');
    }, [language, setLanguage]);

    // Translation function
    const t = useCallback((key: string): string => {
        const translation = translations[language][key as TranslationKey];
        if (translation) {
            return translation;
        }
        // Fallback to English if key not found in current language
        const fallback = translations['en'][key as TranslationKey];
        if (fallback) {
            return fallback;
        }
        // Return the key itself if no translation found
        console.warn(`Translation missing for key: ${key}`);
        return key;
    }, [language]);

    // Re-detect language on mount (in case browser settings changed)
    useEffect(() => {
        const detected = detectBrowserLanguage();
        if (detected !== language && !localStorage.getItem('preferredLanguage')) {
            setLanguageState(detected);
        }
    }, []);

    return {
        language,
        setLanguage,
        toggleLanguage,
        t,
        isSpanish: language === 'es',
        isEnglish: language === 'en',
        isPortuguese: language === 'pt',
        isChinese: language === 'zh',
        isHindi: language === 'hi',
        isArabic: language === 'ar',
        isKorean: language === 'ko',
        isJapanese: language === 'ja',
        isBengali: language === 'bn',
        isGerman: language === 'de',
        isFrench: language === 'fr',
    };
};

export { en, es, pt, zh, hi, ar, ko, ja, bn, de, fr };
export default useLocalization;
